import { createFileRoute } from '@tanstack/react-router'

import {
  AvailabilityDay,
  ExceptionRow,
  ExceptionSheet,
  WindowSheet,
} from '@/components/fotograf/dostupnost'
import { JellyIcon } from '@/components/icons'
import { AppButton, SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  DAY_CHOICES,
  EXCEPTIONS,
  RADIUS_CHOICES,
  WEEK,
  WINDOW_FORM,
} from '@/mocks/fotograf/dostupnost'

/**
 * `stav` is his week (declared or still empty), `info` is which sheet sits on
 * top — the same split the onboarding's Meta step uses, because the two are
 * independent.
 */
type DostupnostSearch = { stav?: 'prazdny'; info?: 'okno' | 'vyjimka' }

const PAGE_HREF = '/fotograf/dostupnost'
const WINDOW_HREF = `${PAGE_HREF}?info=okno`
const EXCEPTION_HREF = `${PAGE_HREF}?info=vyjimka`

export const Route = createFileRoute('/fotograf/dostupnost')({
  validateSearch: (search: Record<string, unknown>): DostupnostSearch => ({
    ...(search.stav === 'prazdny' ? { stav: 'prazdny' as const } : {}),
    ...(search.info === 'okno' || search.info === 'vyjimka'
      ? { info: search.info as 'okno' | 'vyjimka' }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Dostupnost · Aplikace Bitez' }] }),
  component: FotografDostupnost,
})

const EMPTY_WEEK = WEEK.map((entry) => ({ ...entry, windows: [] }))

/**
 * When and where he shoots — rebuilt 2026-08-19.
 *
 * Two things were wrong with it. **The form did not exist**, so the screen was
 * a read-only list of something nobody could create; and it was a week of
 * dashed empty boxes, which made a photographer working four days look at three
 * large reproaches. A free day is now one quiet line, so the windows he has
 * declared are the figure and the free days are the ground.
 *
 * The bigger fix is conceptual: a recurring week is a **rule**, and a rule alone
 * cannot say "next Thursday I'm at a wedding". Until now his only options were
 * to dismantle his whole week or to get booked and pay the no-show penalty
 * (spec §10) — so the week now has **dated exceptions** under it. They are
 * deliberately subtractive only: adding a one-off day is what a window already
 * is.
 *
 * It stays a recurring week and NOT the dated day rail the rest of the app uses
 * for time. The rail draws facts (a shoot happened, a post goes out); this
 * screen draws a standing promise, and putting the two in the same shape would
 * say his Monday rule is a Monday.
 */
function FotografDostupnost() {
  const { stav, info } = Route.useSearch()
  const empty = stav === 'prazdny'
  const week = empty ? EMPTY_WEEK : WEEK
  const exceptions = empty ? [] : EXCEPTIONS

  return (
    <div className="px-4 pt-6 pb-2">
      <header>
        <h1 className="font-display text-3xl leading-[0.9] uppercase">Kdy a kde fotíte?</h1>
        <p className="mt-2 text-xs leading-relaxed text-ink/50">
          {empty
            ? 'Dokud nemáte žádné okno, nemá vám booking co nabídnout — a nedostanete zakázku.'
            : 'Klientům nabídneme jen časy uvnitř vašich oken, a jen tam, kam to stihnete.'}
        </p>
      </header>

      <section className="animate-rise-up mt-7">
        <SectionLabel>Váš týden</SectionLabel>
        <div className="mt-4">
          {week.map((entry) => (
            <AvailabilityDay key={entry.day} entry={entry} addHref={WINDOW_HREF} />
          ))}
        </div>
      </section>

      {/* The exception to the rule, under the rule — never above it. */}
      <section className="animate-rise-up mt-8" style={{ animationDelay: '90ms' }}>
        <SectionLabel>Výjimky</SectionLabel>
        <div className="mt-3.5">
          {exceptions.length > 0 ? (
            <div className="space-y-3.5">
              {exceptions.map((exception) => (
                <ExceptionRow key={exception.date} exception={exception} />
              ))}
            </div>
          ) : (
            <p className="text-[11px] leading-relaxed text-ink/45">
              Jedete na dovolenou nebo máte v pátek jinou práci? Řekněte to sem — váš týden zůstane,
              jak je.
            </p>
          )}
        </div>

        <Link
          href={EXCEPTION_HREF}
          className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 px-4 py-3 text-xs font-bold text-ink/45 transition-colors duration-150 hover:border-ink/30 hover:text-ink/70"
        >
          <JellyIcon name="plus" className="size-3.5" />
          Přidat výjimku
        </Link>
      </section>

      <div className="sticky bottom-0 -mx-4 mt-6 bg-gradient-to-t from-cream via-cream/95 to-transparent px-4 pt-8 pb-3">
        <AppButton href={WINDOW_HREF} className="w-full">
          <JellyIcon name="plus" variant="fill" className="size-3.5" />
          Přidat okno
        </AppButton>
      </div>

      {info === 'okno' && (
        <WindowSheet
          form={WINDOW_FORM}
          days={DAY_CHOICES}
          radii={RADIUS_CHOICES}
          closeHref={PAGE_HREF}
        />
      )}
      {info === 'vyjimka' && <ExceptionSheet closeHref={PAGE_HREF} />}
    </div>
  )
}
